/** Escreve o Documento em PDF, à mão e sem dependências novas.
 *
 *  Segue o mesmo caminho do escritor dos desenhos 2D
 *  (`views2d/lib/drawingToPdf.ts`): PDF 1.4 composto directamente, com
 *  a tabela xref construída a partir dos deslocamentos reais em bytes.
 *
 *  A diferença é que aqui o conteúdo é texto, o que traz dois
 *  problemas que as linhas não tinham:
 *
 *   - acentos. As fontes base-14 do PDF usam a codificação WinAnsi, em
 *     que o "ã" e o "ç" são um byte só mas não o mesmo que em UTF-8.
 *     Por isso há uma conversão explícita;
 *   - parênteses e barras invertidas são sintaxe dentro de uma string
 *     PDF e têm de ser escapados, senão um nome de ficheiro com um
 *     parêntese parte o ficheiro todo.
 */

import {
  MARGEM_MM,
  PAPEL_MM,
  type PaginaComposta,
  type Papel,
  RODAPE_MM,
} from '@viewer/report/documentLayout'

const MM_POR_PONTO = 25.4 / 72

function mmParaPontos(mm: number): number {
  return mm / MM_POR_PONTO
}

/** Tabela dos caracteres que o WinAnsi escreve noutro código que não o
 *  do Unicode. Só os que aparecem em português e nos nomes de
 *  ficheiro. */
const WINANSI: Record<string, number> = {
  '€': 128,
  '‘': 145,
  '’': 146,
  '“': 147,
  '”': 148,
  '•': 149,
  '–': 150,
  '—': 151,
}

/**
 * Passa o texto para WinAnsi e escapa o que é sintaxe do PDF.
 *
 * Um caractere que o WinAnsi não tenha (um emoji, por exemplo) sai como
 * ponto de interrogação em vez de um byte inválido: vale mais um
 * relatório com um "?" do que um PDF que não abre.
 */
export function escaparTextoPdf(texto: string): string {
  let saida = ''
  for (const ch of texto) {
    if (ch === '(' || ch === ')' || ch === '\\') {
      saida += `\\${ch}`
      continue
    }
    const especial = WINANSI[ch]
    if (especial !== undefined) {
      saida += String.fromCharCode(especial)
      continue
    }
    const codigo = ch.codePointAt(0) ?? 63
    saida += codigo <= 255 ? String.fromCharCode(codigo) : '?'
  }
  return saida
}

export interface OpcoesPdf {
  papel: Papel
  /** Escrito no rodapé, à esquerda. */
  rodape: string
}

/** Compõe o PDF e devolve-o em bytes. */
export function documentoParaPdf(
  paginas: readonly PaginaComposta[],
  opcoes: OpcoesPdf,
): Uint8Array {
  const { largura, altura } = PAPEL_MM[opcoes.papel]
  const larguraPt = mmParaPontos(largura)
  const alturaPt = mmParaPontos(altura)
  const topoUtilMm = altura - MARGEM_MM

  const fluxos = paginas.map((pagina, i) => {
    const partes: string[] = []
    for (const linha of pagina.linhas) {
      // O PDF tem a origem em baixo à esquerda; a paginação conta a
      // partir do topo. Daí a subtracção.
      const y = mmParaPontos(topoUtilMm - linha.yMm - linha.tamanhoMm)
      const x = mmParaPontos(MARGEM_MM + linha.xMm)
      const fonte = linha.negrito ? '/F2' : '/F1'
      partes.push(
        `BT ${fonte} ${mmParaPontos(linha.tamanhoMm).toFixed(2)} Tf ` +
          `${x.toFixed(2)} ${y.toFixed(2)} Td (${escaparTextoPdf(linha.texto)}) Tj ET`,
      )
    }
    const rodape = `${opcoes.rodape}    ${i + 1} de ${paginas.length}`
    partes.push(
      `BT /F1 ${mmParaPontos(2.8).toFixed(2)} Tf ` +
        `${mmParaPontos(MARGEM_MM).toFixed(2)} ${mmParaPontos(RODAPE_MM - 5).toFixed(2)} Td ` +
        `0.45 0.45 0.45 rg (${escaparTextoPdf(rodape)}) Tj ET`,
    )
    return partes.join('\n')
  })

  // --- composição do ficheiro -------------------------------------
  const objetos: string[] = []
  const nPaginas = paginas.length
  // 1 catálogo, 2 páginas, 3..(2+n) páginas, depois os fluxos, depois
  // as duas fontes.
  const idPrimeiraPagina = 3
  const idPrimeiroFluxo = idPrimeiraPagina + nPaginas
  const idFonteNormal = idPrimeiroFluxo + nPaginas
  const idFonteNegrito = idFonteNormal + 1

  objetos.push('<< /Type /Catalog /Pages 2 0 R >>')
  const filhos = Array.from(
    { length: nPaginas },
    (_, i) => `${idPrimeiraPagina + i} 0 R`,
  ).join(' ')
  objetos.push(
    `<< /Type /Pages /Kids [${filhos}] /Count ${nPaginas} >>`,
  )
  for (let i = 0; i < nPaginas; i += 1) {
    objetos.push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${larguraPt.toFixed(2)} ${alturaPt.toFixed(2)}] ` +
        `/Resources << /Font << /F1 ${idFonteNormal} 0 R /F2 ${idFonteNegrito} 0 R >> >> ` +
        `/Contents ${idPrimeiroFluxo + i} 0 R >>`,
    )
  }
  for (const fluxo of fluxos) {
    objetos.push(`<< /Length ${fluxo.length} >>\nstream\n${fluxo}\nendstream`)
  }
  objetos.push(
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>',
  )
  objetos.push(
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>',
  )

  let ficheiro = '%PDF-1.4\n'
  const deslocamentos: number[] = []
  objetos.forEach((corpo, i) => {
    deslocamentos.push(ficheiro.length)
    ficheiro += `${i + 1} 0 obj\n${corpo}\nendobj\n`
  })
  const inicioXref = ficheiro.length
  ficheiro += `xref\n0 ${objetos.length + 1}\n0000000000 65535 f \n`
  for (const d of deslocamentos) {
    ficheiro += `${String(d).padStart(10, '0')} 00000 n \n`
  }
  ficheiro +=
    `trailer\n<< /Size ${objetos.length + 1} /Root 1 0 R >>\n` +
    `startxref\n${inicioXref}\n%%EOF\n`

  // Só se escreveram caracteres de código <= 255, por isso cada um
  // ocupa um byte e os deslocamentos medidos em caracteres são os
  // deslocamentos reais.
  const bytes = new Uint8Array(ficheiro.length)
  for (let i = 0; i < ficheiro.length; i += 1) {
    bytes[i] = ficheiro.charCodeAt(i) & 0xff
  }
  return bytes
}
